"use client";

/**
 * Loaded only via `next/dynamic` from the root layout's non-mobile branch, so
 * the Tailwind-compiled app-wide stylesheet is never fetched by mobile-gated
 * visitors (see components/global/mobile-blocker.css for why that matters).
 */
import "@/app/globals.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactNode, useState } from "react";

import { PostHogAuthSync } from "@/lib/analytics/PostHogAuthSync";
import { PostHogPageView } from "@/lib/analytics/PostHogPageView";
import { InoraThemeProvider } from "@/components/theme/inora-theme-provider";
import { ChunkLoadRecovery } from "@/components/errors/ChunkLoadRecovery";
import { DashboardHistoryRestore } from "@/components/providers/DashboardHistoryRestore";
import { NotebookRouteTracker } from "@/components/providers/NotebookRouteTracker";
import { ScrollQueryCacheBridge } from "@/components/providers/ScrollQueryCacheBridge";
import { PlanUsagePrefetch } from "@/components/providers/PlanUsagePrefetch";
import { createAppQueryClient } from "@/lib/react-query/create-query-client";

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createAppQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollQueryCacheBridge />
      <PlanUsagePrefetch />
      <InoraThemeProvider>
        <PostHogPageView />
        <PostHogAuthSync />
        <NotebookRouteTracker />
        <DashboardHistoryRestore />
        <ChunkLoadRecovery>{children}</ChunkLoadRecovery>
      </InoraThemeProvider>
    </QueryClientProvider>
  );
}
